<script setup lang="ts">
import { debounce } from "lodash";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Sidebar from "primevue/sidebar";
import { ref } from "vue";
import { useFilters } from "../composables/useFilters";
import { useSortFilters } from "../composables/useSortFilters";
import { ORDER_DIRECTION_OPTIONS, RATING_OPTIONS, SORT_OPTIONS } from "./constants";
import { anonymousAdvisingFiltersPresets } from "./styles";
import type { Props } from "./types";

defineOptions({
  name: "AnonymousAdvisingFilters",
});

const props = withDefaults(defineProps<Props>(), {});

const visibleRight = ref<boolean>(false);
const isExpanded = ref<boolean>(false);

const {
  ratingFeedback,
  updateRatingFeedback,
  email,
  updateEmail,
  firstName,
  updateFirstName,
  lastName,
  updateLastName,
  title,
  updateTitle,
  clearFilters,
} = useFilters();
const { orderBy, orderDirection, updateOrderBy, updateOrderDirection } = useSortFilters();

const closeSidebar = (): void => {
  visibleRight.value = false;
};

const clear = (): void => {
  clearFilters();
  closeSidebar();
};

const titleDebounce = debounce(updateTitle, 500);
const updateFirstNameDebounce = debounce(updateFirstName, 500);
const updateLastNameDebounce = debounce(updateLastName, 500);
const updateEmailDebounce = debounce(updateEmail, 500);
</script>

<template>
  <div class="flex mt-4">
    <Button
      @click="visibleRight = true"
      @mouseover="isExpanded = true"
      @mouseleave="isExpanded = false"
      outlined
      class="!rounded-full !text-cyan-200 !border-cyan-200 h-8 min-w-12 bg-white"
    >
      <div class="flex align-center justify-center items-center">
        <span class="material-symbols-outlined">tune</span>
        <span class="ml-2 leading-8" v-show="isExpanded">Filters</span>
      </div>
    </Button>

    <Sidebar
      v-model:visible="visibleRight"
      position="right"
      :pt="anonymousAdvisingFiltersPresets.sidebar"
    >
      <template #header>
        <div class="flex justify-between items-center">
          <span class="material-symbols-outlined">tune</span>
          <div class="font-bold uppercase ml-2">Advising Session Filters</div>
        </div>
      </template>

      <Accordion
        :activeIndex="0"
        :multiple="true"
        class="shadow-2xl p-6 h-full overflow-y-scroll"
        :pt="anonymousAdvisingFiltersPresets.accordion"
      >
        <AccordionTab header="Title" :pt="anonymousAdvisingFiltersPresets.accordion">
          <InputText
            :model-value="title ?? ''"
            @update:modelValue="titleDebounce"
            placeholder="Filter by title"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="First Name" :pt="anonymousAdvisingFiltersPresets.accordion">
          <InputText
            :model-value="firstName ?? ''"
            @update:modelValue="updateFirstNameDebounce"
            placeholder="Filter by first name"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Last Name" :pt="anonymousAdvisingFiltersPresets.accordion">
          <InputText
            :model-value="lastName ?? ''"
            @update:modelValue="updateLastNameDebounce"
            placeholder="Filter by last name"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Email" :pt="anonymousAdvisingFiltersPresets.accordion">
          <InputText
            :model-value="email ?? ''"
            @update:modelValue="updateEmailDebounce"
            placeholder="Filter by email"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Rating" :pt="anonymousAdvisingFiltersPresets.accordion">
          <Dropdown
            :model-value="ratingFeedback"
            @update:modelValue="updateRatingFeedback"
            :options="RATING_OPTIONS"
            option-label="name"
            option-value="key"
            placeholder="Select rating"
            show-clear
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Sort By" :pt="anonymousAdvisingFiltersPresets.accordion">
          <Dropdown
            :model-value="orderBy"
            @update:modelValue="updateOrderBy"
            :options="SORT_OPTIONS"
            option-label="name"
            option-value="key"
            class="w-full mb-4"
          />
          <Dropdown
            :model-value="orderDirection"
            @update:modelValue="updateOrderDirection"
            :options="ORDER_DIRECTION_OPTIONS"
            option-label="name"
            option-value="key"
            class="w-full mb-4"
          />
        </AccordionTab>
      </Accordion>

      <div class="flex justify-between m-4">
        <Button
          label="Clear"
          severity="secondary"
          class="ais-ClearRefinements rounded-none bg-transparent border-0 text-teal-900 p-4"
          @click="clear"
          :pt="anonymousAdvisingFiltersPresets.button"
        />
        <Button
          label="Close"
          class="rounded-none bg-teal-900 text-white p-4"
          @click="closeSidebar"
          :pt="anonymousAdvisingFiltersPresets.button"
        />
      </div>
    </Sidebar>
  </div>
</template>

<style scoped>
</style>
